import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users as UsersIcon, 
  Plus, 
  Search, 
  Shield,
  UserCheck,
  Settings,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  XCircle,
  Edit3,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    nom: '',
    prenom: '',
    role: 'commercial',
    module: 'vente',
    telephone: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/register`, newUser);
      loadUsers(); // Reload to get the updated list
      setShowAddForm(false);
      setNewUser({
        email: '',
        password: '',
        nom: '',
        prenom: '',
        role: 'commercial',
        module: 'vente',
        telephone: ''
      });
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'Administrateur',
      commercial: 'Commercial',
      technicien: 'Technicien', 
      comptable: 'Comptable',
      depot: 'Dépôt',
      manager_vente: 'Manager Vente',
      manager_logistique: 'Manager Logistique',
      manager_rh: 'Manager RH',
      employe_vente: 'Employé Vente',
      employe_logistique: 'Employé Logistique',
      employe_rh: 'Employé RH'
    };
    return roles[role] || role;
  };

  const getModuleLabel = (module) => {
    const modules = {
      vente: 'Vente',
      logistique: 'Logistique',
      rh: 'RH'
    };
    return modules[module] || 'Non assigné';
  };

  const getModuleColor = (module) => {
    const colors = {
      vente: 'badge-info',
      logistique: 'badge-success',
      rh: 'badge-warning'
    };
    return colors[module] || 'badge-secondary';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'badge-danger',
      commercial: 'badge-info',
      technicien: 'badge-success',
      comptable: 'badge-warning',
      depot: 'badge-secondary'
    };
    return colors[role] || 'badge-secondary';
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin: Shield,
      commercial: UsersIcon,
      technicien: Settings,
      comptable: UserCheck,
      depot: UserCheck
    };
    return icons[role] || UsersIcon;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = `${user.prenom} ${user.nom} ${user.email}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleCount = (role) => {
    return users.filter(user => user.role === role).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Gestion des Équipes
          </h1>
          <p className="text-gray-600">
            Gérez les utilisateurs et leurs accès au CRM
          </p>
        </div>
        <div className="mt-4 lg:mt-0">
          <button 
            onClick={() => setShowAddForm(true)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            Nouvel utilisateur
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{users.length}</div>
              <div className="stats-label">Total</div>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <UsersIcon className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{getRoleCount('admin')}</div>
              <div className="stats-label">Admins</div>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{getRoleCount('commercial')}</div>
              <div className="stats-label">Commerciaux</div>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <UsersIcon className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{getRoleCount('technicien')}</div>
              <div className="stats-label">Techniciens</div>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value">{users.filter(u => u.actif).length}</div>
              <div className="stats-label">Actifs</div>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            <div className="lg:w-48">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="form-select"
              >
                <option value="all">Tous les rôles</option>
                <option value="admin">Administrateurs</option>
                <option value="commercial">Commerciaux</option>
                <option value="technicien">Techniciens</option>
                <option value="comptable">Comptables</option>
                <option value="depot">Dépôt</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Contact</th>
              <th>Rôle & Module</th>
              <th>Statut</th>
              <th>Créé le</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const RoleIcon = getRoleIcon(user.role);
              
              return (
                <tr key={user.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full flex items-center justify-center">
                        <RoleIcon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.prenom} {user.nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {user.id.substring(0, 8)}...
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-3 h-3 text-gray-400" />
                        <span>{user.email}</span>
                      </div>
                      {user.telephone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <span>{user.telephone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <RoleIcon className="w-4 h-4" />
                        <span className={`badge ${getRoleBadgeColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </div>
                      {user.module && (
                        <span className={`badge ${getModuleColor(user.module)} text-xs`}>
                          Module {getModuleLabel(user.module)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {user.actif ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="badge badge-success">Actif</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span className="badge badge-danger">Inactif</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-3 h-3 text-gray-400" />
                      <span>{new Date(user.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button className="btn btn-secondary btn-sm">
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button className="btn btn-danger btn-sm">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <UsersIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun utilisateur trouvé</p>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Nouvel Utilisateur</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Prénom *</label>
                  <input
                    type="text"
                    value={newUser.prenom}
                    onChange={(e) => setNewUser({...newUser, prenom: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Nom *</label>
                  <input
                    type="text"
                    value={newUser.nom}
                    onChange={(e) => setNewUser({...newUser, nom: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Mot de passe *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="form-input"
                  required
                  minLength="6"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 caractères</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Rôle *</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="form-select"
                    required
                  >
                    <option value="commercial">Commercial</option>
                    <option value="technicien">Technicien</option>
                    <option value="comptable">Comptable</option>
                    <option value="depot">Dépôt</option>
                    <option value="manager_vente">Manager Vente</option>
                    <option value="manager_logistique">Manager Logistique</option>
                    <option value="manager_rh">Manager RH</option>
                    <option value="employe_vente">Employé Vente</option>
                    <option value="employe_logistique">Employé Logistique</option>
                    <option value="employe_rh">Employé RH</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Module *</label>
                  <select
                    value={newUser.module}
                    onChange={(e) => setNewUser({...newUser, module: e.target.value})}
                    className="form-select"
                    required
                  >
                    <option value="">Sélectionner un module</option>
                    <option value="vente">🛒 Vente</option>
                    <option value="logistique">🚚 Logistique</option>
                    <option value="rh">👥 RH</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Téléphone</label>
                <input
                  type="tel"
                  value={newUser.telephone}
                  onChange={(e) => setNewUser({...newUser, telephone: e.target.value})}
                  className="form-input"
                />
              </div>

              <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  Permissions par Module & Rôle
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="font-semibold text-blue-900 mb-2">🛒 Module Vente</div>
                    <div className="space-y-1 text-blue-800">
                      <div><strong>Manager:</strong> Gestion équipe, objectifs</div>
                      <div><strong>Commercial:</strong> Clients, devis, pipeline</div>
                      <div><strong>Employé:</strong> Prospects, suivi client</div>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="font-semibold text-green-900 mb-2">🚚 Module Logistique</div>
                    <div className="space-y-1 text-green-800">
                      <div><strong>Manager:</strong> Planning global, stock</div>
                      <div><strong>Technicien:</strong> Installations, chantiers</div>
                      <div><strong>Dépôt:</strong> Gestion matériel</div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <div className="font-semibold text-purple-900 mb-2">👥 Module RH</div>
                    <div className="space-y-1 text-purple-800">
                      <div><strong>Manager:</strong> Recrutement, formations</div>
                      <div><strong>Comptable:</strong> Paie, administration</div>
                      <div><strong>Employé:</strong> Congés, dossiers RH</div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-orange-50 rounded border border-orange-200">
                  <div className="text-sm text-orange-800">
                    <strong>👤 Administrateur:</strong> Accès complet à tous les modules et fonctionnalités + gestion du switch modulaire
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  <Plus className="w-4 h-4" />
                  Créer l'utilisateur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;